import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MembersScreen.module.css';
import { useMemberExpanded } from '../context/MemberExpandedContext';

import VesselImg from '../assets/members/vessel.png';
import TwoImg from '../assets/members/two.png';
import ThreeImg from '../assets/members/three.png';
import FourImg from '../assets/members/four.png';
import EsperaImg from '../assets/members/espera.png';
import MemberCard from '../components/Members/MemberCard';
import MemberExpanded from '../components/Members/MemberExpanded';

const memberImages = [VesselImg, TwoImg, ThreeImg, FourImg, EsperaImg];

function MembersScreen() {
  const { t } = useTranslation();
  const { setIsExpanded } = useMemberExpanded();
  const membersData = t('members', { returnObjects: true });
  
  const members = membersData.map((member, index) => ({
    ...member,
    image: memberImages[index]
  }));
  const cardRefs = useRef([]);
  const sectionRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [ripple, setRipple] = useState(null);
  const [cardStartPos, setCardStartPos] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [preRevealSelected, setPreRevealSelected] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add('visible');
          });
        },
        { threshold: 0.15 }
      );
      cardRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });
      return () => observer.disconnect();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (phase === 'rippling' || phase === 'expanded') {
      document.body.style.overflow = 'hidden';
      setIsExpanded(true);
    } else {
      document.body.style.overflow = 'auto';
      setIsExpanded(false);
    }
    return () => {
      document.body.style.overflow = 'auto';
      setIsExpanded(false);
    };
  }, [phase, setIsExpanded]);

  const handleCardClick = (index) => {
    if (phase !== 'idle') return;
    const ref = cardRefs.current[index];
    if (!ref) return;

    const rect = ref.getBoundingClientRect();
    const sectionRect = sectionRef.current.getBoundingClientRect();

    setCardStartPos({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
    setRipple({
      x: rect.left + rect.width / 2 - sectionRect.left,
      y: rect.top + rect.height / 2 - sectionRect.top,
    });
    setSelectedIndex(index);
    setPhase('rippling');
    setTimeout(() => setPhase('expanded'), 1000);
  };

  const handleClose = (closingIndex) => {
    setPhase('closing');

    requestAnimationFrame(() => {
      cardRefs.current.forEach((ref, i) => {
        if (ref && i !== closingIndex) ref.classList.add('visible');
      });
    });

    setTimeout(() => {
      setPreRevealSelected(true);
      requestAnimationFrame(() => {
        const ref = cardRefs.current[closingIndex];
        if (ref) ref.classList.add('visible');
      });
    }, 650);

    setTimeout(() => {
      setPhase('idle');
      setSelectedIndex(null);
      setRipple(null);
      setCardStartPos(null);
      setPreRevealSelected(false);
      requestAnimationFrame(() => {
        cardRefs.current.forEach((ref) => {
          if (ref) ref.classList.add('visible');
        });
      });
    }, 1000);
  };

  return (
    <section className={styles.hero} ref={sectionRef}>
      <div className={styles.smoke1} />
      <div className={styles.smoke2} />
      <div className={styles.smoke3} />

      {ripple && phase !== 'idle' && (
        <div
          className={`${styles.rippleOverlay} ${phase === 'closing' ? styles.rippleClose : ''}`}
          style={{
            '--ripple-x': `${ripple.x}px`,
            '--ripple-y': `${ripple.y}px`,
          }}
        />
      )}

      <h2 className={`${styles.heading} ${phase !== 'idle' && phase !== 'closing' ? styles.fadeOut : ''}`}>
        {t('nav.members')}
      </h2>

      <div className={styles.cardsContainer}>
        {members.map((member, index) => (
          <MemberCard
            key={member.name}
            name={member.name}
            role={member.role}
            image={member.image}
            cardRef={(el) => (cardRefs.current[index] = el)}
            delay={`${index * 0.15}s`}
            onClick={() => handleCardClick(index)}
            hide={phase !== 'idle' && phase !== 'closing' && index !== selectedIndex}
            invisible={(phase === 'rippling' || phase === 'expanded' || (phase === 'closing' && !preRevealSelected)) && index === selectedIndex}
          />
        ))}
      </div>

      {selectedIndex !== null && (phase === 'rippling' || phase === 'expanded' || phase === 'closing') && cardStartPos && (
        <MemberCard
          name={members[selectedIndex].name}
          role={members[selectedIndex].role}
          image={members[selectedIndex].image}
          cardRef={null}
          delay="0s"
          floating
          startPos={cardStartPos}
          expanded={phase === 'expanded'}
          closing={false}
        />
      )}

      {phase === 'expanded' && selectedIndex !== null && (
        <MemberExpanded
          member={members[selectedIndex]}
          onClose={() => handleClose(selectedIndex)}
        />
      )}
    </section>
  );
}

export default MembersScreen;